/**
 * TickerDisplay - Renders parsed Kalshi ticker information
 *
 * Supports multiple display variants for different contexts.
 */

import { cn } from '@/lib/infra/utils';
import { parseTicker, formatDateShort } from '@/lib/kalshiTicker';
import type { ParsedTicker } from '@/lib/kalshiTicker';

export type TickerDisplayVariant = 'compact' | 'full' | 'inline';

interface TickerDisplayProps {
  /** The raw ticker string to parse and display */
  ticker: string;

  /** Optional pre-parsed ticker (to avoid re-parsing) */
  parsed?: ParsedTicker;

  /** Display variant */
  variant?: TickerDisplayVariant;

  /** Optional market title to show as fallback/supplement */
  title?: string;

  /** Additional CSS classes */
  className?: string;

  /** Show the raw ticker below the parsed info */
  showRaw?: boolean;
}

/**
 * Renders a parsed Kalshi ticker with smart formatting
 */
export function TickerDisplay({
  ticker,
  parsed: parsedProp,
  variant = 'compact',
  title,
  className,
  showRaw = true,
}: TickerDisplayProps) {
  'use memo'; // #196 React Compiler wave A1 — docs/react-compiler-clusters/wave-a1-sparkline-tickerdisplay.md
  // #201: manual memo removed — this component is compiled (#196 wave A1) and the compiler
  // guards this on `$[0] !== parsedProp || $[1] !== ticker`, which is what the dependency
  // array said. `parseTicker` has no cache and allocates per call, so `parsed`'s stability
  // is now the compiler's job alone — and it matters: `parsed` feeds TickerFullDisplay,
  // which is neither compiled nor memo-wrapped, so the cached element is the only reason
  // that child still bails out. Measured unchanged (full·stable stays at 2 units of work,
  // not 40) in docs/react-compiler-clusters/manual-memo-cleanup-201.md.
  const parsed = parsedProp ?? parseTicker(ticker);

  // Label priority: fullLabel (has team names) > title prop > shortLabel
  const isUnknown = parsed.category === 'unknown';
  const hasFullNames =
    parsed.fullLabel && parsed.fullLabel !== parsed.raw && parsed.fullLabel !== parsed.shortLabel;
  const mainLabel =
    isUnknown && title ? title : hasFullNames ? parsed.fullLabel : title || parsed.shortLabel;

  if (variant === 'inline') {
    return <span className={cn('font-mono text-sm', className)}>{mainLabel}</span>;
  }

  if (variant === 'full') {
    return (
      <div className={cn('flex flex-col gap-0.5', className)}>
        <TickerFullDisplay
          parsed={parsed}
          title={title}
        />
        {showRaw && <span className="text-muted-foreground font-mono text-xs">{parsed.raw}</span>}
      </div>
    );
  }

  // Default: compact variant
  return (
    <div className={cn('flex flex-col leading-tight', className)}>
      <span className="text-foreground text-sm font-medium">{mainLabel}</span>
      {showRaw && <span className="text-muted-foreground font-mono text-xs">{parsed.raw}</span>}
    </div>
  );
}

/**
 * Full variant with all parsed details
 */
function TickerFullDisplay({ parsed, title }: { parsed: ParsedTicker; title?: string }) {
  const { category, date, game, player } = parsed;

  // Category badge
  const categoryLabel = getCategoryLabel(category);
  const categoryColor = getCategoryColor(category);

  return (
    <div className="flex flex-col gap-1">
      {/* Category badge + date */}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            categoryColor
          )}
        >
          {categoryLabel}
        </span>
        {date && (
          <span className="text-muted-foreground font-mono text-xs">{formatDateShort(date)}</span>
        )}
      </div>

      {/* Game info */}
      {game && (
        <div className="text-sm font-medium">
          {game.awayName ?? game.awayCode} @ {game.homeName ?? game.homeCode}
        </div>
      )}

      {/* Player info */}
      {player && (
        <div className="text-sm">
          <span className="text-muted-foreground">{player.teamCode}</span>{' '}
          <span className="font-medium">{player.displayName ?? player.playerId}</span>
        </div>
      )}

      {/* Fallback to title if no structured data */}
      {!game && !player && title && <div className="text-sm font-medium">{title}</div>}
    </div>
  );
}

function getCategoryLabel(category: ParsedTicker['category']): string {
  const labels: Record<ParsedTicker['category'], string> = {
    // NBA
    'nba-game': 'NBA',
    'nba-spread': 'NBA SPR',
    'nba-total': 'NBA TOT',
    'nba-season': 'NBA',
    // WNBA
    'wnba-game': 'WNBA',
    'wnba-spread': 'WNBA SPR',
    'wnba-total': 'WNBA TOT',
    'wnba-season': 'WNBA',
    // NFL
    'nfl-game': 'NFL',
    'nfl-spread': 'NFL SPR',
    'nfl-total': 'NFL TOT',
    'nfl-pass-yds': 'NFL PASS YDS',
    'nfl-pass-td': 'NFL PASS TD',
    'nfl-pass-att': 'NFL PASS ATT',
    'nfl-pass-comp': 'NFL COMP',
    'nfl-pass-int': 'NFL INT THROWN',
    'nfl-rec-yds': 'NFL REC YDS',
    'nfl-rec': 'NFL REC',
    'nfl-long-rec': 'NFL LONG REC',
    'nfl-rush-yds': 'NFL RUSH YDS',
    'nfl-rush-att': 'NFL RUSH ATT',
    'nfl-long-rush': 'NFL LONG RUSH',
    'nfl-rush-rec-yds': 'NFL RUSH+REC',
    'nfl-td': 'NFL TD',
    'nfl-2td': '2+ TD',
    'nfl-first-td': '1st TD',
    'nfl-anytime-td': 'ANY TD',
    'nfl-dst-td': 'D/ST TD',
    'nfl-tackles': 'NFL TKL',
    'nfl-sacks': 'NFL SACK',
    'nfl-int': 'NFL INT',
    'nfl-superbowl': 'SB',
    // NHL
    'nhl-game': 'NHL',
    'nhl-spread': 'NHL SPR',
    'nhl-total': 'NHL TOT',
    // MLB
    'mlb-game': 'MLB',
    'mlb-spread': 'MLB SPR',
    'mlb-total': 'MLB TOT',
    // MLB player props + event markets
    'mlb-hits': 'MLB HITS',
    'mlb-hr': 'MLB HR',
    'mlb-rbi': 'MLB RBI',
    'mlb-tb': 'MLB TB',
    'mlb-sb': 'MLB SB',
    'mlb-ks': 'MLB K',
    'mlb-outs': 'MLB OUTS',
    'mlb-hrr': 'MLB H+R+RBI',
    'mlb-teamtotal': 'MLB TEAM TOT',
    'mlb-rfi': 'MLB YRFI',
    'mlb-extras': 'MLB EXTRAS',
    // Series
    'nba-series': 'NBA SER',
    'wnba-series': 'WNBA SER',
    'nhl-series': 'NHL SER',
    'mlb-series': 'MLB SER',
    // NCAA
    'ncaa-game': 'NCAA',
    'ncaa-spread': 'NCAA SPR',
    'ncaa-total': 'NCAA TOT',
    // Tennis
    'tennis-match': 'TENNIS',
    // UFC / MMA
    'ufc-fight': 'UFC',
    // Boxing / Esports / Cricket / CFB
    'boxing-fight': 'BOX',
    'esports-game': 'ESPORTS',
    'cricket-match': 'CRICKET',
    'cfb-game': 'CFB',
    'cfb-spread': 'CFB SPR',
    'cfb-total': 'CFB TOT',
    'soccer-game': 'SOCCER',
    'soccer-spread': 'SOCCER SPR',
    'soccer-total': 'SOCCER TOT',
    // Other
    politics: 'POL',
    crypto: 'CRYPTO',
    polymarket: 'POLY',
    unknown: '?',
  };
  return labels[category];
}

function getCategoryColor(category: ParsedTicker['category']): string {
  const colors: Record<ParsedTicker['category'], string> = {
    // NBA - orange
    'nba-game': 'bg-orange-500/20 text-orange-400',
    'nba-spread': 'bg-orange-500/20 text-orange-400',
    'nba-total': 'bg-orange-500/20 text-orange-400',
    'nba-season': 'bg-orange-500/20 text-orange-400',
    // WNBA - fuchsia (matches hydra dashboard)
    'wnba-game': 'bg-fuchsia-500/20 text-fuchsia-400',
    'wnba-spread': 'bg-fuchsia-500/20 text-fuchsia-400',
    'wnba-total': 'bg-fuchsia-500/20 text-fuchsia-400',
    'wnba-season': 'bg-fuchsia-500/20 text-fuchsia-400',
    // NFL - blue
    'nfl-game': 'bg-blue-500/20 text-blue-400',
    'nfl-spread': 'bg-blue-500/20 text-blue-400',
    'nfl-total': 'bg-blue-500/20 text-blue-400',
    'nfl-pass-yds': 'bg-blue-500/20 text-blue-400',
    'nfl-pass-td': 'bg-blue-500/20 text-blue-400',
    'nfl-pass-att': 'bg-blue-500/20 text-blue-400',
    'nfl-pass-comp': 'bg-blue-500/20 text-blue-400',
    'nfl-pass-int': 'bg-blue-500/20 text-blue-400',
    'nfl-rec-yds': 'bg-blue-500/20 text-blue-400',
    'nfl-rec': 'bg-blue-500/20 text-blue-400',
    'nfl-long-rec': 'bg-blue-500/20 text-blue-400',
    'nfl-rush-yds': 'bg-blue-500/20 text-blue-400',
    'nfl-rush-att': 'bg-blue-500/20 text-blue-400',
    'nfl-long-rush': 'bg-blue-500/20 text-blue-400',
    'nfl-rush-rec-yds': 'bg-blue-500/20 text-blue-400',
    'nfl-td': 'bg-blue-500/20 text-blue-400',
    'nfl-2td': 'bg-blue-500/20 text-blue-400',
    'nfl-first-td': 'bg-blue-500/20 text-blue-400',
    'nfl-anytime-td': 'bg-blue-500/20 text-blue-400',
    'nfl-dst-td': 'bg-blue-500/20 text-blue-400',
    'nfl-tackles': 'bg-blue-500/20 text-blue-400',
    'nfl-sacks': 'bg-blue-500/20 text-blue-400',
    'nfl-int': 'bg-blue-500/20 text-blue-400',
    'nfl-superbowl': 'bg-blue-500/20 text-blue-400',
    // NHL - cyan
    'nhl-game': 'bg-cyan-500/20 text-cyan-400',
    'nhl-spread': 'bg-cyan-500/20 text-cyan-400',
    'nhl-total': 'bg-cyan-500/20 text-cyan-400',
    // MLB - red
    'mlb-game': 'bg-red-500/20 text-red-400',
    'mlb-spread': 'bg-red-500/20 text-red-400',
    'mlb-total': 'bg-red-500/20 text-red-400',
    'mlb-hits': 'bg-red-500/20 text-red-400',
    'mlb-hr': 'bg-red-500/20 text-red-400',
    'mlb-rbi': 'bg-red-500/20 text-red-400',
    'mlb-tb': 'bg-red-500/20 text-red-400',
    'mlb-sb': 'bg-red-500/20 text-red-400',
    'mlb-ks': 'bg-red-500/20 text-red-400',
    'mlb-outs': 'bg-red-500/20 text-red-400',
    'mlb-hrr': 'bg-red-500/20 text-red-400',
    'mlb-teamtotal': 'bg-red-500/20 text-red-400',
    'mlb-rfi': 'bg-red-500/20 text-red-400',
    'mlb-extras': 'bg-red-500/20 text-red-400',
    // Series — amber to read as the "playoff" overlay (matches the
    // existing series-row badge in the moneyline table).
    'nba-series': 'bg-amber-500/20 text-amber-400',
    'wnba-series': 'bg-amber-500/20 text-amber-400',
    'nhl-series': 'bg-amber-500/20 text-amber-400',
    'mlb-series': 'bg-amber-500/20 text-amber-400',
    // NCAA - purple
    'ncaa-game': 'bg-purple-500/20 text-purple-400',
    'ncaa-spread': 'bg-purple-500/20 text-purple-400',
    'ncaa-total': 'bg-purple-500/20 text-purple-400',
    // Tennis - green
    'tennis-match': 'bg-green-500/20 text-green-400',
    // UFC / MMA - red
    'ufc-fight': 'bg-red-500/20 text-red-400',
    // Boxing - rose, Esports - cyan, Cricket - lime, CFB - amber
    'boxing-fight': 'bg-rose-500/20 text-rose-400',
    'esports-game': 'bg-cyan-500/20 text-cyan-400',
    'cricket-match': 'bg-lime-500/20 text-lime-400',
    'cfb-game': 'bg-amber-500/20 text-amber-400',
    'cfb-spread': 'bg-amber-500/20 text-amber-400',
    'cfb-total': 'bg-amber-500/20 text-amber-400',
    'soccer-game': 'bg-emerald-500/20 text-emerald-400',
    'soccer-spread': 'bg-emerald-500/20 text-emerald-400',
    'soccer-total': 'bg-emerald-500/20 text-emerald-400',
    // Other
    politics: 'bg-red-500/20 text-red-400',
    crypto: 'bg-yellow-500/20 text-yellow-400',
    // Polymarket brand-ish magenta — distinct from kalshi sport colors.
    polymarket: 'bg-pink-500/20 text-pink-400',
    unknown: 'bg-muted text-muted-foreground',
  };
  return colors[category];
}

export default TickerDisplay;
